import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, Globe, Shield, Award, TrendingUp, Users, BadgeCheck, Leaf, Building2 } from 'lucide-react';
import './AboutPage.css';

const AboutPage: React.FC = () => {
  const values = [
    {
      icon: <Award size={48} />,
      title: 'Honour and Integrity',
      description: 'We uphold the highest ethical standards in all our dealings'
    },
    {
      icon: <TrendingUp size={48} />,
      title: 'Forward Thinking',
      description: 'Innovation and adaptability drive our continuous growth'
    },
    {
      icon: <Zap size={48} />,
      title: 'Quality and Efficiency',
      description: 'Excellence in service delivery with optimized processes'
    },
    {
      icon: <Users size={48} />,
      title: 'Customer-Focused',
      description: 'Your needs and satisfaction are at the heart of everything we do'
    },
    {
      icon: <BadgeCheck size={48} />,
      title: 'Professionalism',
      description: 'Maintaining the highest standards of service and conduct'
    },
    {
      icon: <Leaf size={48} />,
      title: 'Employee Development',
      description: 'Investing in our people to build a stronger future'
    }
  ];

  return (
    <div className="about-page">
      <div className="about-hero">
        <div className="container">
          <h1 className="page-title">About CN Terminal Company Limited</h1>
          <p className="page-subtitle">
            A licensed Customs House Agency providing comprehensive shipping and logistics 
            solutions with a commitment to speed, reliability, and excellence.
          </p>
        </div>
      </div>

      <div className="container">
        <section className="about-story-section">
          <div className="story-content">
            <div className="story-text">
              <h2 className="section-heading">Our Story</h2>
              <div className="story-paragraphs">
                <p>
                  CN Terminal Company Limited is a licensed Customs House Agency providing comprehensive 
                  shipping and logistics solutions. We have demonstrated consistent growth in operations, 
                  revenue, and workforce, developing global partnerships and broadening our international clientele.
                </p>
                <p>
                  Today, we stand as a trusted partner for businesses of all sizes, providing end-to-end 
                  logistics solutions with a commitment to excellence, innovation, and customer satisfaction.
                </p>
              </div>
            </div>
            <div className="story-images">
              <div className="story-image-wrapper">
                <img 
                  src="https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=1200&q=80" 
                  alt="Shipping operations" 
                  className="story-image"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="why-choose-section">
          <h2 className="section-heading">Why Choose Us</h2>
          <div className="why-choose-grid">
            <div className="why-choose-card">
              <div className="why-choose-icon">
                <Zap size={40} />
              </div>
              <h3>Speed & Convenience</h3>
              <p>Streamlined processes ensure quick turnaround times without compromising on quality.</p>
            </div>
            <div className="why-choose-card">
              <div className="why-choose-icon">
                <Shield size={40} />
              </div>
              <h3>Trustworthy & Proactive</h3>
              <p>Transparent and consistent deliverables with proactive problem-solving for smooth operations.</p>
            </div>
            <div className="why-choose-card">
              <div className="why-choose-icon">
                <Globe size={40} />
              </div>
              <h3>Global Network</h3>
              <p>Strategic partnerships with leading carriers worldwide ensuring reliable service.</p>
            </div>
            <div className="why-choose-card">
              <div className="why-choose-icon">
                <Award size={40} />
              </div>
              <h3>Licensed Expertise</h3>
              <p>Licensed Customs House Agency with expert knowledge and regulatory compliance.</p>
            </div>
          </div>
        </section>

        <section className="values-section">
          <h2 className="section-heading">Our Core Values</h2>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card-expanded">
                <div className="value-icon-expanded">{value.icon}</div>
                <h3 className="value-title-expanded">{value.title}</h3>
                <p className="value-description-expanded">{value.description}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section 
        className="stats-section"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=1920&q=80')`,
          backgroundColor: `rgba(3, 34, 105, 0.8)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="container">
          <div className="stats-grid-expanded">
            <div className="stat-card-expanded">
              <div className="stat-icon-expanded">
                <Building2 size={48} />
              </div>
              <div className="stat-number-expanded">Licensed</div>
              <div className="stat-label-expanded">Customs House Agency</div>
            </div>
            <div className="stat-card-expanded">
              <div className="stat-icon-expanded">
                <Globe size={48} />
              </div>
              <div className="stat-number-expanded">Global</div>
              <div className="stat-label-expanded">Partnerships</div>
            </div>
            <div className="stat-card-expanded">
              <div className="stat-icon-expanded">
                <TrendingUp size={48} />
              </div>
              <div className="stat-number-expanded">Growing</div>
              <div className="stat-label-expanded">International Clientele</div>
            </div>
            <div className="stat-card-expanded">
              <div className="stat-icon-expanded">
                <Users size={48} />
              </div>
              <div className="stat-number-expanded">Experienced</div>
              <div className="stat-label-expanded">Professional Team</div>
            </div>
          </div>
        </div>
      </section>

      <div className="about-cta">
        <div className="container">
          <h2>Partner With Us</h2>
          <p>Join hundreds of satisfied clients who trust CN Terminal for their logistics needs.</p>
          <Link to="/contact" className="btn btn-primary">Get in Touch</Link>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
